import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, GraduationCap, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const DIPLOMAS = [
  { id: 'deap', label: 'DEAP - Auxiliaire de Puériculture' },
  { id: 'deeje', label: 'DEEJE - Éducateur de Jeunes Enfants' },
  { id: 'cap-aepe', label: 'CAP AEPE - Petite Enfance' },
  { id: 'deas', label: 'DEAS - Aide-Soignant' },
  { id: 'caferuis', label: 'CAFERUIS - Encadrement' },
  { id: 'bac-assp', label: 'BAC ASSP - Soins et Services' },
  { id: 'tp', label: 'TP IEPE - Petite Enfance' }
];

const RegisterPage = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    password: '',
    confirmPassword: '',
    role: 'candidate',
    targetDiploma: '',
    livret1Recevabilite: '',
    livret2Status: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptReglement, setAcceptReglement] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.password || !formData.confirmPassword || !formData.targetDiploma || !formData.livret1Recevabilite || !formData.livret2Status) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (!acceptTerms || !acceptReglement) {
      toast.error('Veuillez accepter les conditions et le r\u00e8glement int\u00e9rieur');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caract\u00e8res');
      return;
    }

    setLoading(true);
    
    try {
      const res = await fetch(`${window.location.origin}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          phone: formData.phone,
          address: formData.address,
          targetDiploma: formData.targetDiploma,
          livret1Recevabilite: formData.livret1Recevabilite,
          livret2Status: formData.livret2Status
        })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Erreur lors de l\'inscription');
      }

      const data = await res.json();
      const userData = {
        id: data.user.id,
        email: data.user.email,
        name: data.user.name,
        role: data.user.role,
        token: data.access_token
      };

      onLogin(userData);
      toast.success('Inscription r\u00e9ussie ! Bienvenue !');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      paddingTop: '80px',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg-page)',
      padding: '2rem 1.2rem'
    }}>
      <div className="container" style={{ maxWidth: '450px' }}>
        <div className="card" style={{ padding: '2rem' }}>
          <h1 className="heading-1" style={{ marginBottom: '0.5rem', textAlign: 'center' }}>
            Créer un compte
          </h1>
          <p className="body-small" style={{ 
            textAlign: 'center',
            marginBottom: '2rem',
            color: 'var(--text-secondary)'
          }}>
            Rejoignez-nous et commencez votre parcours VAE
          </p>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="body-small" style={{ 
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 500
              }}>
                Nom complet
              </label>
              <div style={{ position: 'relative' }}>
                <User 
                  size={18} 
                  style={{ 
                    position: 'absolute',
                    left: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-muted)'
                  }} 
                />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Jean Dupont"
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem 0.75rem 3rem',
                    border: '1px solid var(--border-input)',
                    borderRadius: '0.5rem',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'}
                />
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label className="body-small" style={{ 
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 500
              }}>
                Email
              </label>
              <div style={{ position: 'relative' }}>
                <Mail 
                  size={18} 
                  style={{ 
                    position: 'absolute',
                    left: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-muted)'
                  }} 
                />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="votre@email.fr"
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem 0.75rem 3rem',
                    border: '1px solid var(--border-input)',
                    borderRadius: '0.5rem',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'}
                />
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label className="body-small" style={{ 
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 500
              }}>
                Téléphone
              </label>
              <div style={{ position: 'relative' }}>
                <Phone 
                  size={18} 
                  style={{ 
                    position: 'absolute',
                    left: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-muted)'
                  }} 
                />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="06 12 34 56 78"
                  data-testid="phone-input"
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem 0.75rem 3rem',
                    border: '1px solid var(--border-input)',
                    borderRadius: '0.5rem',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'}
                />
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label className="body-small" style={{ 
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 500
              }}>
                Adresse postale
              </label>
              <div style={{ position: 'relative' }}>
                <MapPin 
                  size={18} 
                  style={{ 
                    position: 'absolute',
                    left: '1rem',
                    top: '0.85rem',
                    color: 'var(--text-muted)'
                  }} 
                />
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="12 rue de la Paix, 75002 Paris"
                  data-testid="address-input"
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem 0.75rem 3rem',
                    border: '1px solid var(--border-input)',
                    borderRadius: '0.5rem',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'}
                />
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label className="body-small" style={{ 
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 500
              }}>
                Dipl\u00f4me souhait\u00e9
              </label>
              <div style={{ position: 'relative' }}>
                <GraduationCap 
                  size={18} 
                  style={{ 
                    position: 'absolute',
                    left: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-muted)',
                    pointerEvents: 'none'
                  }} 
                />
                <select
                  name="targetDiploma"
                  value={formData.targetDiploma}
                  onChange={handleChange}
                  data-testid="diploma-select"
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem 0.75rem 3rem',
                    border: '1px solid var(--border-input)',
                    borderRadius: '0.5rem',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    background: 'white',
                    cursor: 'pointer',
                    appearance: 'auto'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'}
                >
                  <option value="">-- Sélectionnez un diplôme --</option>
                  {DIPLOMAS.map(d => (
                    <option key={d.id} value={d.id}>{d.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label className="body-small" style={{ 
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 500
              }}>
                Avez-vous la recevabilité du Livret 1 ?
              </label>
              <select
                name="livret1Recevabilite"
                value={formData.livret1Recevabilite}
                onChange={handleChange}
                data-testid="livret1-select"
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '1px solid var(--border-input)',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  background: 'white',
                  cursor: 'pointer'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'}
              >
                <option value="">-- Sélectionnez --</option>
                <option value="oui">Oui</option>
                <option value="non">Non</option>
              </select>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label className="body-small" style={{ 
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 500
              }}>
                Où en est votre Livret 2 ?
              </label>
              <select
                name="livret2Status"
                value={formData.livret2Status}
                onChange={handleChange}
                data-testid="livret2-select"
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '1px solid var(--border-input)',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  background: 'white',
                  cursor: 'pointer'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'}
              >
                <option value="">-- Sélectionnez --</option>
                <option value="a_faire">À faire</option>
                <option value="commence">Déjà commencé</option>
                <option value="depose">Déjà déposé</option>
              </select>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label className="body-small" style={{ 
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 500
              }}>
                Mot de passe
              </label>
              <div style={{ position: 'relative' }}>
                <Lock 
                  size={18} 
                  style={{ 
                    position: 'absolute',
                    left: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-muted)'
                  }} 
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  style={{
                    width: '100%',
                    padding: '0.75rem 3rem 0.75rem 3rem',
                    border: '1px solid var(--border-input)',
                    borderRadius: '0.5rem',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--text-muted)',
                    padding: '0.25rem'
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label className="body-small" style={{ 
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 500
              }}>
                Confirmer le mot de passe
              </label>
              <div style={{ position: 'relative' }}>
                <Lock 
                  size={18} 
                  style={{ 
                    position: 'absolute',
                    left: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-muted)'
                  }} 
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem 0.75rem 3rem',
                    border: '1px solid var(--border-input)',
                    borderRadius: '0.5rem',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'}
                />
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer' }}>
                <input 
                  type="checkbox" 
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  style={{ marginTop: '0.25rem' }} 
                  data-testid="accept-terms-checkbox"
                />
                <span className="body-small">
                  J'accepte les{' '}
                  <Link to="/terms" target="_blank" style={{ textDecoration: 'none', color: 'var(--text-primary)', fontWeight: 500 }}>
                    conditions d'utilisation
                  </Link>
                  {' '}et la{' '}
                  <Link to="/privacy" target="_blank" style={{ textDecoration: 'none', color: 'var(--text-primary)', fontWeight: 500 }}>
                    politique de confidentialité
                  </Link>
                </span>
              </label>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer' }}>
                <input 
                  type="checkbox" 
                  checked={acceptReglement}
                  onChange={(e) => setAcceptReglement(e.target.checked)}
                  style={{ marginTop: '0.25rem' }} 
                  data-testid="accept-reglement-checkbox"
                />
                <span className="body-small">
                  J'accepte le{' '}
                  <Link to="/reglement" target="_blank" style={{ textDecoration: 'none', color: 'var(--text-primary)', fontWeight: 500 }}>
                    règlement intérieur
                  </Link>
                </span>
              </label>
            </div>

            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
              style={{
                width: '100%',
                justifyContent: 'center',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Inscription...' : 'S\'inscrire'}
            </button>
          </form>

          <div style={{ 
            marginTop: '1.5rem',
            textAlign: 'center',
            paddingTop: '1.5rem',
            borderTop: '1px solid var(--border-light)'
          }}>
            <p className="body-small">
              Déjà un compte ?{' '}
              <Link to="/login" style={{ 
                textDecoration: 'none',
                color: 'var(--text-primary)',
                fontWeight: 500
              }}>
                Se connecter
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
